import { useEffect, useState } from "react";
import axios from "axios";
import Card from "../Components/Card";
import RedButton from "../Components/RedButton";
import BlueButton from "../Components/BlueButton";
import EntitySearchSelect from "../Components/EntitySearchSelect";
import { BRAND } from "../css/brand";
import { fetchRoles } from "../Data/ROLES";
import Swal from "sweetalert2";

export default function PullBackFlow() {
  const [rolesMap, setRolesMap] = useState({});
  const [fromType, setFromType] = useState("");
  const [fromId, setFromId] = useState("");
  const [packageList, setPackageList] = useState([]); // ✅ new state
  const [selectedPackage, setSelectedPackage] = useState(""); // ✅ selected package

  const [startKey, setStartKey] = useState("");
  const [endKey, setEndKey] = useState("");
  const [reason, setReason] = useState("");
  const [destRole, setDestRole] = useState("");
  const [destId, setDestId] = useState("");

  const [allowedFromRoles, setAllowedFromRoles] = useState([]);
  const [allowedDestRoles, setAllowedDestRoles] = useState([]);

  const [fromEntities, setFromEntities] = useState([]); // From entity list
  const [destEntities, setDestEntities] = useState([]); // Destination entity list

  const [loadingFrom, setLoadingFrom] = useState(false);
  const [loadingDest, setLoadingDest] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false); // ✅ loading state for packages

  // ✅ Load roles once
  useEffect(() => {
    (async () => {
      const roleData = await fetchRoles();
      setRolesMap(roleData);
      const allowed = getAllowedRolesBySession(roleData);
      setAllowedFromRoles(allowed);
      setAllowedDestRoles(allowed);
    })();
  }, []);

  // ✅ Session-based allowed roles
  const getAllowedRolesBySession = (rolesMap) => {
    const authUserString = sessionStorage.getItem("authUserData1");
    const authUser = JSON.parse(authUserString);
    const userRole = authUser?.data?.[0]?.role;

    const hierarchy = {
      2: ["Company", "CNF", "Super Stockist", "Distributor", "FOS","Retailer"],
      3: ["Company", "Super Stockist", "Distributor", "Retailer"],
      4: ["Company", "Distributor", "Retailer"],
      5: ["Company", "Retailer"],
    };

    return hierarchy[userRole] || [];
  };

  // ✅ Common fetch function for roles (CNF, SS, Dist, Retailer)
  const fetchEntitiesByRole = async (role, setter) => {
    if (!role || role === "Company") return setter([]);

    setter([]); // clear old
    const apiUrl = "https://spillas.com/api/v1/org_user/search?company_id=26&login_id=26";

    const prefixMap = {
      CNF: "CNF",
      "Super Stockist": "SS",
      Distributor: "DIST",
      FOS: "FOS",
      Retailer: "RTL",
    };

    const prefix = prefixMap[role];
    if (!prefix) return setter([]);

    try {
      const res = await axios.get(apiUrl);
      const data = res.data?.data || res.data || [];
      const filtered = data
        .filter((item) => item.org_code?.startsWith(prefix))
        .map((item) => ({
          id: item.id,
          name: item.business_name || item.name || "Unnamed",
          org_code: item.org_code,
          label: `${item.org_code} - ${item.business_name || item.name}`,
        }));
      setter(filtered);
    } catch (err) {
      console.error(`❌ ${role} fetch failed:`, err);
      setter([]);
    }
  };

  // ✅ Fetch "From" entity list
  useEffect(() => {
    if (!fromType) return;
    setLoadingFrom(true);
    fetchEntitiesByRole(fromType, setFromEntities).finally(() => setLoadingFrom(false));
  }, [fromType]);

  // ✅ Fetch "Destination" entity list
  useEffect(() => {
    if (!destRole) return;
    if (destRole === "Company") {
      setDestEntities([]);
      return;
    }
    setLoadingDest(true);
    fetchEntitiesByRole(destRole, setDestEntities).finally(() => setLoadingDest(false));
  }, [destRole]);

  // ✅ Fetch package list for selected "From Entity"
  useEffect(() => {
  if (!fromId?.id) {
    setPackageList([]);
    setSelectedPackage("");
    return;
  }

  const fetchPackages = async () => {
    setLoadingPackages(true);
    try {
      const res = await axios.get(
        `https://spillas.com/api/v1/keys/all-my-package-list?receiver_id=${fromId.id}`
      );
      const packages = res.data?.data || res.data || [];
      setPackageList(packages);

      // ✅ Don’t auto-select — let the user choose any package
      // If you want to keep a previous selection if still valid:
      // if (!packages.some(pkg => pkg.package_name === selectedPackage)) setSelectedPackage("");
    } catch (err) {
      console.error("❌ Package fetch failed:", err);
      setPackageList([]);
    } finally {
      setLoadingPackages(false);
    }
  };

  fetchPackages();
}, [fromId]);


  const canPickDestId = destRole !== "Company";

  // ✅ Submit handler
   const handleSubmit = async () => {
  const authUserString = sessionStorage.getItem("authUserData1");
  const authUser = JSON.parse(authUserString)?.data?.[0];
  if (!authUser)
    return Swal.fire("Session Expired", "Please login again.", "warning");

  const sessionRole = Number(authUser.role);
  const sessionId = authUser.id;
  const sessionCompanyId = authUser.company_id;
  const sessionOrgCode = authUser.org_code;
  const sessionBusinessName = authUser.business_name;
  const sessionRoleName = rolesMap[sessionRole] || "Unknown";

  if (!fromId)
    return Swal.fire("Missing Field", "Select 'From Entity'.", "info");
  if (!selectedPackage)
    return Swal.fire("Missing Field", "Select a package.", "info");
  if (!startKey)
    return Swal.fire("Missing Field", "Enter number of keys to pull back.", "info");
  if (canPickDestId && !destId)
    return Swal.fire("Missing Field", "Select a destination entity.", "info");

  const pkg = packageList.find((p) => p.id === selectedPackage);
  if (!pkg)
    return Swal.fire("Invalid Package", "Please select a valid package.", "error");

  const qty = Number(startKey);
  const amount = Number(pkg.key_rate) * qty;

  // --- ✅ Role Mapping Logic ---
  const getRoleId = (targetRoleNum) => {
    if (sessionRole === targetRoleNum) return sessionId;

    const destRoleNum = rolesMapReverse(destRole, rolesMap); // convert "Distributor" → 5
    if (destRoleNum === targetRoleNum) return destId?.id;

    return null;
  };

  const cnf_id = getRoleId(3);
  const ss_id = getRoleId(4);
  const dist_id = getRoleId(5);

  const receiverIsCompany = destRole === "Company";
  const receiver_id = receiverIsCompany ? sessionId : destId?.id;
  const receiver_code = receiverIsCompany ? sessionOrgCode : destId?.org_code;
  const receiver_name = receiverIsCompany ? sessionBusinessName : destId?.name;
  const receiver_role = receiverIsCompany
    ? sessionRole
    : rolesMapReverse(destRole, rolesMap);

  const company_id = receiverIsCompany ? sessionId : sessionCompanyId;

  const payload = {
    amount,
    cnf_id,
    ss_id,
    dist_id,
    company_id,
    created_by_code: sessionOrgCode,
    created_by_id: sessionId,
    created_by_name: sessionBusinessName,
    created_by_role: sessionRole,
    key_rate: pkg.key_rate,
    key_type: pkg.key_type,
    mode: 2,
    package_name: pkg.package_name,
    product_id: pkg.product_id,
    qty,
    receiver_code,
    receiver_id,
    receiver_name,
    role: receiver_role,
    sender_code: fromId.org_code,
    sender_id: fromId.id,
    sender_name: fromId.name,
    sender_package_id: pkg.id,
    user_package_id: pkg.user_package_id,
  };

  console.log("🟢 Pullback Payload:", payload);

  try {
    const res = await axios.post("https://spillas.com/api/v1/keys/assign", payload);

    if (res) {
      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Pullback and reassignment successful!",
        confirmButtonColor: "#3085d6",
      });
      // optional reset
      setFromType("");
      setFromId("");
      setSelectedPackage("");
      setStartKey("");
      setDestRole("");
      setDestId("");
      setReason("");
      setPackageList([]);
    } else {
      Swal.fire("Failed", res.data?.message || "Unknown error", "error");
    }
  } catch (err) {
    console.error("❌ API Error:", err);
    Swal.fire("Error", "Something went wrong while assigning keys.", "error");
  }
};


  // ✅ helper to find role number by name
  const rolesMapReverse = (roleName, map) => {
    const found = Object.entries(map).find(([num, name]) => name === roleName);
    return found ? Number(found[0]) : null;
  };


  return (
    <Card brand={BRAND} title="Key Pull Back (with Re-assignment)">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
        {/* ✅ FROM TYPE */}
        <select
          value={fromType}
          onChange={(e) => setFromType(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm h-10"
        >
          <option value="">Select Type</option>
          {allowedFromRoles
            .filter((role) => role !== "Company")
            .map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
        </select>
        {/* ✅ FROM ENTITY */}
        <div className="sm:col-span-2">
          {loadingFrom ? (
            <div className="text-sm text-slate-500">Loading {fromType} list…</div>
          ) : (
            <EntitySearchSelect
              options={fromEntities}
              value={fromId}
              onChange={setFromId}
              placeholder={`Search ${fromType || "Entity"}…`}
              isDisabled={!fromType} // disable until fromType selected
            />
          )}
        </div>
        {/* ✅ PACKAGE SELECTOR + KEY INPUT */}
        <div className="sm:col-span-2 flex flex-col gap-2">

          {/* Package Dropdown */}
          <select
            value={selectedPackage}
            onChange={(e) => {
              setSelectedPackage(Number(e.target.value));
              setStartKey("");
            }}
            disabled={!fromId}
            className="rounded-lg border px-3 py-2 text-sm w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">Select Package</option>
            {loadingPackages ? (
              <option disabled>Loading packages…</option>
            ) : packageList.length === 0 ? (
              <option disabled>No packages found</option>
            ) : (
              packageList.map((pkg, i) => (
                <option key={i} value={pkg.id}>
                  {pkg.package_name} - {pkg.qty}
                </option>
              ))
            )}
          </select>

          {/* Key Input */}
          <input
            type="number"
            value={startKey}
            min={1}
            disabled={!selectedPackage}
            onChange={(e) => {
              const val = e.target.value;
              const selectedPkg = packageList.find(pkg => pkg.id === selectedPackage);
              const maxQty = selectedPkg?.qty || Infinity;

              if (/^\d*$/.test(val) && Number(val) <= maxQty) {
                setStartKey(val);
              }
            }}
            placeholder="Enter Key to pullback"
            className="rounded-lg border px-3 py-2 text-sm w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
          />

          {selectedPackage && (
            <p className="text-xs text-slate-500">
              Max allowed:{" "}
              <span className="font-medium text-slate-700">
                {packageList.find((pkg) => pkg.id === selectedPackage)?.qty || 0}
              </span>
            </p>
          )}
        </div>
        {/* ✅ DESTINATION SECTION */}
        <div className="sm:col-span-6 grid grid-cols-6 gap-3 mt-3">

          {/* Destination Role */}
          <div className="sm:col-span-2">
            <select
              value={destRole}
              onChange={(e) => {
                setDestRole(e.target.value);
                setDestId("");
              }}
              disabled={!startKey}
              className="rounded-lg border px-3 py-2 text-sm h-10 w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select Destination</option>
              {allowedDestRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>

          {/* Destination Entity */}
          <div className="sm:col-span-2">
            {canPickDestId ? (
              loadingDest ? (
                <div className="text-sm text-slate-500">Loading {destRole} list…</div>
              ) : (
                <EntitySearchSelect
                  options={destEntities.filter(
                    (entity) => entity.id !== fromId?.id
                  )}
                  value={destId}
                  onChange={(selected) => {
                    if (selected?.id === fromId?.id) {
                      alert("You cannot select the same user as both source and destination.");
                      setDestId("");
                    } else {
                      setDestId(selected);
                    }
                  }}
                  placeholder={`Search ${destRole || "Entity"}…`}
                  isDisabled={!destRole}
                />
              )
            ) : (
              <div className="flex items-center text-sm text-slate-600">
                Assigning back to <b className="ml-1">Company</b>
              </div>
            )}
          </div>

          {/* ✅ Reason (Read-only) */}
          <div className="sm:col-span-2">
            <input
              value={
                startKey && fromId && (destId || destRole === "Company")
                  ? `${startKey} key${Number(startKey) > 1 ? "s" : ""} will be pulled back from ${
                      fromId.name || fromId.label || "Selected User"
                    } to ${
                      destRole === "Company"
                        ? "Company"
                        : destId?.name || destId?.label || "Selected Destination"
                    }`
                  : reason
              }
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason or summary (optional)"
              className="rounded-lg border px-3 py-2 text-sm w-full"
              readOnly // 👈 always read-only
            />
          </div>

        </div>
        {/* ✅ BUTTONS */}
        <div className="sm:col-span-6 flex items-center justify-end gap-2">
          <RedButton
            brand={BRAND}
            onClick={() => {
              setFromType("");
              setFromId("");
              setSelectedPackage("");
              setStartKey("");
              setDestRole("");
              setDestId("");
              setReason("");
              setPackageList([]);
            }}
          >
            Reset
          </RedButton>
          <BlueButton
            brand={BRAND}
            onClick={handleSubmit}
            disabled={!destId && destRole !== "Company"}
          >
            Pull Back & Assign
          </BlueButton>
        </div>
      </div>
    </Card>
  );
}



// import { useEffect, useState, useMemo } from "react";
// import axios from "axios";
// import Card from "../Components/Card";
// import RedButton from "../Components/RedButton";
// import BlueButton from "../Components/BlueButton";
// import EntitySearchSelect from "../Components/EntitySearchSelect";
// import { BRAND } from "../css/brand";
// import { fetchRoles } from "../Data/ROLES";

// export default function PullBackFlow() {
//   const [rolesMap, setRolesMap] = useState({});
//   const [fromType, setFromType] = useState("");
//   const [fromId, setFromId] = useState("");
//   const [keyFamily, setKeyFamily] = useState("Regular");
//   const [startKey, setStartKey] = useState("");
//   const [endKey, setEndKey] = useState("");
//   const [reason, setReason] = useState("");
//   const [destRole, setDestRole] = useState("");
//   const [destId, setDestId] = useState("");

//   const [allowedFromRoles, setAllowedFromRoles] = useState([]);
//   const [allowedDestRoles, setAllowedDestRoles] = useState([]);

//   const [fromEntities, setFromEntities] = useState([]); // From entity list
//   const [destEntities, setDestEntities] = useState([]); // Destination entity list

//   const [loadingFrom, setLoadingFrom] = useState(false);
//   const [loadingDest, setLoadingDest] = useState(false);

//   // ✅ Load roles once
//   useEffect(() => {
//     (async () => {
//       const roleData = await fetchRoles();
//       setRolesMap(roleData);
//       const allowed = getAllowedRolesBySession(roleData);
//       setAllowedFromRoles(allowed);
//       setAllowedDestRoles(allowed);
//     })();
//   }, []);

//   // ✅ Session-based allowed roles
//   const getAllowedRolesBySession = (rolesMap) => {
//     const authUserString = sessionStorage.getItem("authUserData1");
//     const authUser = JSON.parse(authUserString);
//     const userRole = authUser?.data?.[0]?.role;

//     const hierarchy = {
//       2: ["Company", "CNF", "Super Stockist", "Distributor", "Retailer"],
//       3: ["Company", "Super Stockist", "Distributor", "Retailer"],
//       4: ["Company", "Distributor", "Retailer"],
//       5: ["Company", "Retailer"],
//     };

//     return hierarchy[userRole] || [];
//   };

//   // ✅ Common fetch function for roles (CNF, SS, Dist, Retailer)
//   const fetchEntitiesByRole = async (role, setter) => {
//     if (!role || role === "Company") return setter([]);

//     setter([]); // clear old
//     const apiUrl = "https://spillas.com/api/v1/org_user/search?company_id=26&login_id=26";

//     const prefixMap = {
//       CNF: "CNF",
//       "Super Stockist": "SS",
//       Distributor: "DIST",
//       Retailer: "RTL",
//     };

//     const prefix = prefixMap[role];
//     if (!prefix) return setter([]);

//     try {
//       const res = await axios.get(apiUrl);
//       const data = res.data?.data || res.data || [];
//       const filtered = data
//         .filter((item) => item.org_code?.startsWith(prefix))
//         .map((item) => ({
//           id: item.id,
//           name: item.business_name || item.name || "Unnamed",
//           org_code: item.org_code,
//           label: `${item.org_code} - ${item.business_name || item.name}`,
//         }));
//       setter(filtered);
//     } catch (err) {
//       console.error(`❌ ${role} fetch failed:`, err);
//       setter([]);
//     }
//   };

//   // ✅ Fetch "From" entity list
//   useEffect(() => {
//     if (!fromType) return;
//     setLoadingFrom(true);
//     fetchEntitiesByRole(fromType, setFromEntities).finally(() => setLoadingFrom(false));
//   }, [fromType]);

//   // ✅ Fetch "Destination" entity list
//   useEffect(() => {
//     if (!destRole) return;
//     if (destRole === "Company") {
//       setDestEntities([]);
//       return;
//     }
//     setLoadingDest(true);
//     fetchEntitiesByRole(destRole, setDestEntities).finally(() => setLoadingDest(false));
//   }, [destRole]);

//   const canPickDestId = destRole !== "Company";

//   // ✅ Submit handler
//   const handleSubmit = () => {
//     if (!fromId) return alert("Select 'From Entity'.");
//     if (!startKey || !endKey) return alert("Enter key range.");
//     if (canPickDestId && !destId) return alert("Select a destination entity.");

//     alert(
//       `Pulled back ${keyFamily} keys ${startKey}…${endKey} from ${fromType} ${fromId.name}\nAssigned to ${destRole}${
//         canPickDestId ? ` ${destId.name}` : " (Company)"
//       }\n${reason ? `Reason: ${reason}` : ""}`
//     );

//     setFromId("");
//     setDestId("");
//     setStartKey("");
//     setEndKey("");
//     setReason("");
//   };

//   return (
//     <Card brand={BRAND} title="Key Pull Back (with Re-assignment)">
//       <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
//         {/* ✅ FROM TYPE Dropdown */}
//         <select
//           value={fromType}
//           onChange={(e) => setFromType(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm"
//         >
//           <option value="">Select Type</option>
//           {allowedFromRoles
//             .filter((role) => role !== "Company")
//             .map((role) => (
//               <option key={role} value={role}>
//                 {role}
//               </option>
//             ))}
//         </select>

//         {/* ✅ FROM ENTITY SEARCH */}
//         <div className="sm:col-span-2">
//           {loadingFrom ? (
//             <div className="text-sm text-slate-500">Loading {fromType} list…</div>
//           ) : (
//             <EntitySearchSelect
//               options={fromEntities}
//               value={fromId}
//               onChange={setFromId}
//               placeholder={`Search ${fromType || "Entity"}…`}
//             />
//           )}
//         </div>

//         {/* ✅ Key Family */}
//         <select
//           value={keyFamily}
//           onChange={(e) => setKeyFamily(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm"
//         >
//           <option>Regular</option>
//           <option>Smart</option>
//         </select>

//         {/* ✅ Start Key */}
//         <input
//           value={startKey}
//           onChange={(e) => setStartKey(e.target.value)}
//           placeholder="Start Key"
//           className="rounded-lg border px-3 py-2 text-sm"
//         />

//         {/* ✅ END KEY */}
//         <input
//           value={endKey}
//           onChange={(e) => setEndKey(e.target.value)}
//           placeholder="End Key"
//           className="rounded-lg border px-3 py-2 text-sm"
//         />

//         {/* ✅ DESTINATION ROLE + ENTITY */}
//         <div className="sm:col-span-6 grid grid-cols-1 gap-3 sm:grid-cols-6 mt-3">
//           <select
//             value={destRole}
//             onChange={(e) => {
//               setDestRole(e.target.value);
//               setDestId("");
//             }}
//             className="rounded-lg border px-3 py-2 text-sm"
//           >
//             <option value="">Select Destination</option>
//             {allowedDestRoles.map((role) => (
//               <option key={role} value={role}>
//                 {role}
//               </option>
//             ))}
//           </select>

//           {/* ✅ DESTINATION ENTITY SEARCH */}
//           {canPickDestId ? (
//             <div className="sm:col-span-3">
//               {loadingDest ? (
//                 <div className="text-sm text-slate-500">Loading {destRole} list…</div>
//               ) : (
//                 <EntitySearchSelect
//                   options={destEntities}
//                   value={destId}
//                   onChange={setDestId}
//                   placeholder={`Search ${destRole || "Entity"}…`}
//                 />
//               )}
//             </div>
//           ) : (
//             <div className="sm:col-span-3 flex items-center text-sm text-slate-600">
//               Assigning back to <b className="ml-1">Company</b>
//             </div>
//           )}

//           {/* ✅ Reason */}
//           <input
//             value={reason}
//             onChange={(e) => setReason(e.target.value)}
//             placeholder="Reason (optional)"
//             className="sm:col-span-2 rounded-lg border px-3 py-2 text-sm"
//           />

//           {/* ✅ Buttons */}
//           <div className="sm:col-span-6 flex items-center justify-end gap-2">
//             <RedButton
//               brand={BRAND}
//               onClick={() => {
//                 setFromId("");
//                 setDestId("");
//                 setStartKey("");
//                 setEndKey("");
//                 setReason("");
//               }}
//             >
//               Reset
//             </RedButton>
//             <BlueButton brand={BRAND} onClick={handleSubmit}>
//               Pull Back & Assign
//             </BlueButton>
//           </div>
//         </div>
//       </div>
//     </Card>
//   );
// }


// import { useEffect, useState, useMemo } from "react";
// import axios from "axios";
// import Card from "../Components/Card";
// import RedButton from "../Components/RedButton";
// import BlueButton from "../Components/BlueButton";
// import EntitySearchSelect from "../Components/EntitySearchSelect";
// import { BRAND } from "../css/brand";
// import { seed } from "../Data/seed";
// import { fetchRoles } from "../Data/ROLES";

// export default function PullBackFlow() {
//   const [rolesMap, setRolesMap] = useState({});
//   const [fromType, setFromType] = useState("");
//   const [fromId, setFromId] = useState("");
//   const [keyFamily, setKeyFamily] = useState("Regular");
//   const [startKey, setStartKey] = useState("");
//   const [endKey, setEndKey] = useState("");
//   const [reason, setReason] = useState("");

//   const [destRole, setDestRole] = useState("");
//   const [destId, setDestId] = useState("");

//   const [allowedFromRoles, setAllowedFromRoles] = useState([]);
//   const [allowedDestRoles, setAllowedDestRoles] = useState([]);

//   const [dynamicEntities, setDynamicEntities] = useState([]); // 👈 for CNF API data
//   const [loadingEntities, setLoadingEntities] = useState(false);

//   // ✅ Load all roles once
//   useEffect(() => {
//     (async () => {
//       const roleData = await fetchRoles();
//       setRolesMap(roleData);
//       setAllowedFromRoles(getAllowedRolesBySession(roleData));
//       setAllowedDestRoles(getAllowedRolesBySession(roleData));
//     })();
//   }, []);

//   // ✅ Session-based allowed roles
//   const getAllowedRolesBySession = (rolesMap) => {
//     const authUserString = sessionStorage.getItem("authUserData1");
//     const authUser = JSON.parse(authUserString);
//     const userRole = authUser?.data?.[0]?.role;

//     const hierarchy = {
//       2: ["Company", "CNF", "Super Stockist", "Distributor", "Retailer"],
//       3: ["Company", "Super Stockist", "Distributor", "Retailer"],
//       4: ["Company", "Distributor", "Retailer"],
//       5: ["Company", "Retailer"],
//     };

//     return hierarchy[userRole] || [];
//   };

//   // ✅ Fetch CNF data dynamically when selected
//   // ✅ Fetch data dynamically when "fromType" changes
//   useEffect(() => {
//     if (!fromType) return;

//     setLoadingEntities(true);

//     // Define API URL once
//     const apiUrl = "https://spillas.com/api/v1/org_user/search?company_id=26&login_id=26";

//     axios
//       .get(apiUrl)
//       .then((res) => {
//         const data = res.data?.data || res.data || [];

//         // ✅ Role-based prefix filtering
//         const prefixMap = {
//           CNF: "CNF",
//           "Super Stockist": "SS",
//           Distributor: "DIST",
//           Retailer: "RTL",
//         };

//         const prefix = prefixMap[fromType];
//         const filtered = data
//           .filter((item) => item.org_code?.startsWith(prefix))
//           .map((item) => ({
//             id: item.id,
//             name: item.business_name || item.name || "Unnamed",
//             org_code: item.org_code,
//             label: `${item.org_code} - ${item.business_name || item.name}`,
//           }));

//         setDynamicEntities(filtered);
//       })
//       .catch((err) => {
//         console.error(`❌ ${fromType} Fetch Failed:`, err);
//       })
//       .finally(() => setLoadingEntities(false));
//   }, [fromType]);


//   const canPickDestId = destRole !== "Company";

//   const entityPool = useMemo(
//     () => ({
//       Company: [],
//       CNF: dynamicEntities, // 👈 use API result for CNF
//       "Super Stockist": dynamicEntities,
//       Distributor: dynamicEntities,
//       "Micro Distributor": dynamicEntities,
//       Retailer: dynamicEntities,
//     }),
//     [dynamicEntities]
//   );

//   // ✅ Handle Submit
//   const handleSubmit = () => {
//     if (!fromId) return alert("Select 'From Entity' by ID.");
//     if (!startKey || !endKey) return alert("Enter key range.");
//     if (canPickDestId && !destId)
//       return alert("Select a destination entity.");

//     alert(
//       `Pulled back ${keyFamily} keys ${startKey}…${endKey} from ${fromType} ${fromId}\nAssigned to ${destRole}${
//         canPickDestId ? ` ${destId}` : " (Company)"
//       }\n${reason ? `Reason: ${reason}` : ""}`
//     );

//     setFromId("");
//     setStartKey("");
//     setEndKey("");
//     setDestId("");
//     setReason("");
//   };

//   return (
//     <Card brand={BRAND} title="Key Pull Back (with Re-assignment)">
//       <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
//         {/* ✅ FROM TYPE Dropdown */}
//         <select
//           value={fromType}
//           onChange={(e) => setFromType(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm"
//         >
//           <option value="">Select Type</option>
//           {allowedFromRoles
//             .filter((role) => role !== "Company")
//             .map((role) => (
//               <option key={role} value={role}>
//                 {role}
//               </option>
//             ))}
//         </select>

//         {/* ✅ Entity Search (CNF pulls from API) */}
//         <div className="sm:col-span-2">
//           {loadingEntities ? (
//             <div className="text-sm text-slate-500">Loading CNF list…</div>
//           ) : (
//             <EntitySearchSelect
//               options={entityPool[fromType] || []}
//               value={fromId}
//               onChange={(selected) => setFromId(selected)} // store full object
//               placeholder={`Search ${fromType || "Entity"}…`}
//             />
//           )}
//         </div>

//         <select
//           value={keyFamily}
//           onChange={(e) => setKeyFamily(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm"
//         >
//           <option>Regular</option>
//           <option>Smart</option>
//         </select>

//         <input
//           value={startKey}
//           onChange={(e) => setStartKey(e.target.value)}
//           placeholder="Start Key"
//           className="rounded-lg border px-3 py-2 text-sm"
//         />

//         {/* ✅ DESTINATION ROLE Dropdown */}
//         <div className="sm:col-span-6 grid grid-cols-1 gap-3 sm:grid-cols-6">
//           <select
//             value={destRole}
//             onChange={(e) => {
//               setDestRole(e.target.value);
//               setDestId("");
//             }}
//             className="rounded-lg border px-3 py-2 text-sm"
//           >
//             <option value="">Select Destination</option>
//             {allowedDestRoles.map((role) => (
//               <option key={role} value={role}>
//                 {role}
//               </option>
//             ))}
//           </select>

//           {/* ✅ Entity Picker */}
//           {canPickDestId ? (
//             <div className="sm:col-span-3">
//               <EntitySearchSelect
//                 options={entityPool[destRole] || []}
//                 value={destId}
//                 onChange={setDestId}
//                 placeholder={`Search ${destRole}…`}
//               />
//             </div>
//           ) : (
//             <div className="sm:col-span-3 flex items-center text-sm text-slate-600">
//               Assigning back to <b className="ml-1">Company</b>
//             </div>
//           )}

//           {/* ✅ Reason */}
//           <input
//             value={reason}
//             onChange={(e) => setReason(e.target.value)}
//             placeholder="Reason (optional)"
//             className="sm:col-span-2 rounded-lg border px-3 py-2 text-sm"
//           />

//           {/* ✅ Buttons */}
//           <div className="sm:col-span-6 flex items-center justify-end gap-2">
//             <RedButton
//               brand={BRAND}
//               onClick={() => {
//                 setFromId("");
//                 setDestId("");
//                 setStartKey("");
//                 setEndKey("");
//                 setReason("");
//               }}
//             >
//               Reset
//             </RedButton>
//             <BlueButton brand={BRAND} onClick={handleSubmit}>
//               Pull Back & Assign
//             </BlueButton>
//           </div>
//         </div>
//       </div>
//     </Card>
//   );
// }


// import { useEffect, useState, useMemo } from "react";
// import Card from "../Components/Card";
// import RedButton from "../Components/RedButton";
// import BlueButton from "../Components/BlueButton";
// import EntitySearchSelect from "../Components/EntitySearchSelect";
// import { BRAND } from "../css/brand";
// import { seed } from "../Data/seed";
// import { fetchRoles } from "../Data/ROLES";

// export default function PullBackFlow() {
//   const [rolesMap, setRolesMap] = useState({});
//   const [fromType, setFromType] = useState("");
//   const [fromId, setFromId] = useState("");
//   const [keyFamily, setKeyFamily] = useState("Regular");
//   const [startKey, setStartKey] = useState("");
//   const [endKey, setEndKey] = useState("");
//   const [reason, setReason] = useState("");

//   const [destRole, setDestRole] = useState("");
//   const [destId, setDestId] = useState("");

//   const [allowedFromRoles, setAllowedFromRoles] = useState([]);
//   const [allowedDestRoles, setAllowedDestRoles] = useState([]);

//   // ✅ Load all roles once from API
//   useEffect(() => {
//     (async () => {
//       const roleData = await fetchRoles();
//       setRolesMap(roleData);
//       setAllowedFromRoles(getAllowedRolesBySession(roleData));
//       setAllowedDestRoles(getAllowedRolesBySession(roleData)); // same for dest initially
//     })();
//   }, []);

//   // ✅ Get allowed roles from session dynamically
//   const getAllowedRolesBySession = (rolesMap) => {
//     const authUserString = sessionStorage.getItem("authUserData1");
//     const authUser = JSON.parse(authUserString);
//     const userRole = authUser?.data?.[0]?.role;

//     // Role hierarchy (based on role name, not ID)
//     const hierarchy = {
//       2: ["Company", "CNF", "Super Stockist", "Distributor", "Retailer"],
//       3: ["Company", "Super Stockist", "Distributor", "Retailer"],
//       4: ["Company", "Distributor", "Retailer"],
//       5: ["Company", "Retailer"],
//     };

//     return hierarchy[userRole] || [];
//   };

//   // ✅ Determine if destination requires ID (Company = false)
//   const canPickDestId = destRole !== "Company";

//   const entityPool = useMemo(
//     () => ({
//       Company: [],
//       CNF: seed.cnfs.map((x) => ({ id: x.id, name: x.name })),
//       "Super Stockist": seed.sstks.map((x) => ({ id: x.id, name: x.name })),
//       Distributor: seed.dists.map((x) => ({ id: x.id, name: x.name })),
//       "Micro Distributor": seed.mdists.map((x) => ({ id: x.id, name: x.name })),
//       Retailer: seed.rets.map((x) => ({ id: x.id, name: x.name })),
//     }),
//     []
//   );

//   // ✅ Handle Submit
//   const handleSubmit = () => {
//     if (!fromId) return alert("Select 'From Entity' by ID.");
//     if (!startKey || !endKey) return alert("Enter key range.");
//     if (canPickDestId && !destId)
//       return alert("Select a destination entity.");

//     alert(
//       `Pulled back ${keyFamily} keys ${startKey}…${endKey} from ${fromType} ${fromId}\nAssigned to ${destRole}${
//         canPickDestId ? ` ${destId}` : " (Company)"
//       }\n${reason ? `Reason: ${reason}` : ""}`
//     );

//     setFromId("");
//     setStartKey("");
//     setEndKey("");
//     setDestId("");
//     setReason("");
//   };

//   return (
//     <Card brand={BRAND} title="Key Pull Back (with Re-assignment)">
//       <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
//         {/* ✅ FROM TYPE Dropdown */}
//         <select
//           value={fromType}
//           onChange={(e) => setFromType(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm"
//         >
//           <option value="">Select Type</option>
//           {allowedFromRoles
//             .filter((role) => role !== "Company") // 👈 Exclude Company here
//             .map((role) => (
//               <option key={role} value={role}>
//                 {role}
//               </option>
//             ))}
//         </select>

//         <div className="sm:col-span-2">
//           <EntitySearchSelect
//             options={entityPool[fromType] || []}
//             value={fromId}
//             onChange={setFromId}
//             placeholder={`Search ${fromType || "Entity"}…`}
//           />
//         </div>

//         <select
//           value={keyFamily}
//           onChange={(e) => setKeyFamily(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm"
//         >
//           <option>Regular</option>
//           <option>Smart</option>
//         </select>

//         <input
//           value={startKey}
//           onChange={(e) => setStartKey(e.target.value)}
//           placeholder="Start Key"
//           className="rounded-lg border px-3 py-2 text-sm"
//         />

//         {/* ✅ DESTINATION ROLE Dropdown */}
//         <div className="sm:col-span-6 grid grid-cols-1 gap-3 sm:grid-cols-6">
//           <select
//             value={destRole}
//             onChange={(e) => {
//               setDestRole(e.target.value);
//               setDestId("");
//             }}
//             className="rounded-lg border px-3 py-2 text-sm"
//           >
//             <option value="">Select Destination</option>
//             {allowedDestRoles.map((role) => (
//               <option key={role} value={role}>
//                 {role}
//               </option>
//             ))}
//           </select>

//           {/* ✅ Entity Picker */}
//           {canPickDestId ? (
//             <div className="sm:col-span-3">
//               <EntitySearchSelect
//                 options={entityPool[destRole] || []}
//                 value={destId}
//                 onChange={setDestId}
//                 placeholder={`Search ${destRole}…`}
//               />
//             </div>
//           ) : (
//             <div className="sm:col-span-3 flex items-center text-sm text-slate-600">
//               Assigning back to <b className="ml-1">Company</b>
//             </div>
//           )}

//           {/* ✅ Reason */}
//           <input
//             value={reason}
//             onChange={(e) => setReason(e.target.value)}
//             placeholder="Reason (optional)"
//             className="sm:col-span-2 rounded-lg border px-3 py-2 text-sm"
//           />

//           {/* ✅ Buttons */}
//           <div className="sm:col-span-6 flex items-center justify-end gap-2">
//             <RedButton
//               brand={BRAND}
//               onClick={() => {
//                 setFromId("");
//                 setDestId("");
//                 setStartKey("");
//                 setEndKey("");
//                 setReason("");
//               }}
//             >
//               Reset
//             </RedButton>
//             <BlueButton brand={BRAND} onClick={handleSubmit}>
//               Pull Back & Assign
//             </BlueButton>
//           </div>
//         </div>
//       </div>
//     </Card>
//   );
// }


// ****************************PHASE 1*********************************************


// import { useEffect, useState, useMemo } from "react";
// import Card from "../Components/Card";
// import RedButton from "../Components/RedButton";
// import BlueButton from "../Components/BlueButton";
// import EntitySearchSelect from "../Components/EntitySearchSelect";
// import { BRAND } from "../css/brand";
// import computeAllowedDestRoles from "../Data/computeAllowedDestRoles";
// import { seed } from "../Data/seed";
// import { fetchRoles } from "../Data/ROLES";

// export default function PullBackFlow() {
//   const [roles, setRoles] = useState([]);
//   const [fromType, setFromType] = useState("");
//   const [fromId, setFromId] = useState("");
//   const [keyFamily, setKeyFamily] = useState("Regular");
//   const [startKey, setStartKey] = useState("");
//   const [endKey, setEndKey] = useState("");
//   const [reason, setReason] = useState("");

//   const [destRole, setDestRole] = useState("");
//   const [destId, setDestId] = useState("");

//   const [allowedRoles, setAllowedRoles] = useState([]); // 👈 dynamic dropdown

//   // ✅ Load roles once
//   useEffect(() => {
//     (async () => {
//       const rolesData = await fetchRoles();
//       setRoles(Object.values(rolesData)); // store names
//       setAllowedRoles(getAllowedRolesBySession());
//     })();
//   }, []);

//   // ✅ Read user role from session and return allowed role names
//   const getAllowedRolesBySession = () => {
//     const authUserString = sessionStorage.getItem("authUserData1");
//     const authUser = JSON.parse(authUserString);
//     const userRole = authUser?.data?.[0]?.role;

//     // mapping based on role hierarchy
//     const roleHierarchy = {
//       2: ["CNF", "Super Stockist", "Distributor", "Retailer"],
//       3: ["Super Stockist", "Distributor", "Retailer"],
//       4: ["Distributor", "Retailer"],
//       5: ["Retailer"],
//     };

//     return roleHierarchy[userRole] || [];
//   };

//   // ✅ update destination role list when fromType changes
//   const allowedDestRoles = computeAllowedDestRoles(fromType);
//   const canPickDestId = destRole !== "Company";

//   useEffect(() => {
//     const list = computeAllowedDestRoles(fromType);
//     setDestRole(list[0]);
//     setFromId("");
//     setDestId("");
//   }, [fromType]);

//   const entityPool = useMemo(() => ({
//     Company: [],
//     CNF: seed.cnfs.map((x) => ({ id: x.id, name: x.name })),
//     "Super Stockist": seed.sstks.map((x) => ({ id: x.id, name: x.name })),
//     Distributor: seed.dists.map((x) => ({ id: x.id, name: x.name })),
//     "Micro Distributor": seed.mdists.map((x) => ({ id: x.id, name: x.name })),
//     Retailer: seed.rets.map((x) => ({ id: x.id, name: x.name })),
//   }), []);

//   const handleSubmit = () => {
//     if (!fromId) return alert("Select 'From Entity' by ID.");
//     if (!startKey || !endKey) return alert("Enter key range.");
//     if (canPickDestId && !destId) return alert("Select a destination entity.");

//     alert(`Pulled back ${keyFamily} keys ${startKey}…${endKey} from ${fromType} ${fromId}\nAssigned to ${destRole}${canPickDestId ? ` ${destId}` : " (Company)"}\n${reason ? `Reason: ${reason}` : ""}`);

//     setFromId(""); setStartKey(""); setEndKey(""); setDestId(""); setReason("");
//   };

//   return (
//     <Card brand={BRAND} title="Key Pull Back (with Re-assignment)">
//       <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">

//         {/* ✅ Dynamic From Type Dropdown */}
//         <select
//           value={fromType}
//           onChange={(e) => setFromType(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm"
//         >
//           <option value="">Select Type</option>
//           {allowedRoles.map((role) => (
//             <option key={role} value={role}>
//               {role}
//             </option>
//           ))}
//         </select>

//         <div className="sm:col-span-2">
//           <EntitySearchSelect
//             options={entityPool[fromType] || []}
//             value={fromId}
//             onChange={setFromId}
//             placeholder={`Search ${fromType || "Entity"}…`}
//           />
//         </div>

//         <select
//           value={keyFamily}
//           onChange={(e) => setKeyFamily(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm"
//         >
//           <option>Regular</option>
//           <option>Smart</option>
//         </select>

//         <input
//           value={startKey}
//           onChange={(e) => setStartKey(e.target.value)}
//           placeholder="Start Key"
//           className="rounded-lg border px-3 py-2 text-sm"
//         />

//         {/* Destination Role and Inputs */}
//         <div className="sm:col-span-6 grid grid-cols-1 gap-3 sm:grid-cols-6">
//           <select
//             value={destRole}
//             onChange={(e) => {
//               setDestRole(e.target.value);
//               setDestId("");
//             }}
//             className="rounded-lg border px-3 py-2 text-sm"
//           >
//             {allowedDestRoles.map((r) => (
//               <option key={r}>{r}</option>
//             ))}
//           </select>

//           {canPickDestId ? (
//             <div className="sm:col-span-3">
//               <EntitySearchSelect
//                 options={entityPool[destRole]}
//                 value={destId}
//                 onChange={setDestId}
//                 placeholder={`Search ${destRole}…`}
//               />
//             </div>
//           ) : (
//             <div className="sm:col-span-3 flex items-center text-sm text-slate-600">
//               Assigning back to <b className="ml-1">Company</b>
//             </div>
//           )}

//           <input
//             value={reason}
//             onChange={(e) => setReason(e.target.value)}
//             placeholder="Reason (optional)"
//             className="sm:col-span-2 rounded-lg border px-3 py-2 text-sm"
//           />

//           <div className="sm:col-span-6 flex items-center justify-end gap-2">
//             <RedButton
//               brand={BRAND}
//               onClick={() => {
//                 setFromId("");
//                 setDestId("");
//                 setStartKey("");
//                 setEndKey("");
//                 setReason("");
//               }}
//             >
//               Reset
//             </RedButton>
//             <BlueButton brand={BRAND} onClick={handleSubmit}>
//               Pull Back & Assign
//             </BlueButton>
//           </div>
//         </div>
//       </div>
//     </Card>
//   );
// }


// *****************************original code*****************************************


// import { useEffect, useState, useMemo } from "react";
// import Card from "../Components/Card";
// import RedButton from "../Components/RedButton";
// import BlueButton from "../Components/BlueButton";
// import EntitySearchSelect from "../Components/EntitySearchSelect";

// import { BRAND } from "../css/brand";

// import computeAllowedDestRoles from "../Data/computeAllowedDestRoles";
// import { seed } from "../Data/seed";

// export default function PullBackFlow() {
//   const [fromType, setFromType] = useState("Retailer");
//   const [fromId, setFromId] = useState("");
//   const [keyFamily, setKeyFamily] = useState("Regular");
//   const [startKey, setStartKey] = useState("");
//   const [endKey, setEndKey] = useState("");
//   const [reason, setReason] = useState("");

//   const allowedDestRoles = computeAllowedDestRoles(fromType);
//   const [destRole, setDestRole] = useState(allowedDestRoles[0]);
//   const [destId, setDestId] = useState("");

//   const entityPool = useMemo(() => ({
//     Company: [],
//     CNF: seed.cnfs.map((x) => ({ id: x.id, name: x.name })),
//     "Super Stockist": seed.sstks.map((x) => ({ id: x.id, name: x.name })),
//     Distributor: seed.dists.map((x) => ({ id: x.id, name: x.name })),
//     "Micro Distributor": seed.mdists.map((x) => ({ id: x.id, name: x.name })),
//     Retailer: seed.rets.map((x) => ({ id: x.id, name: x.name })),
//   }), []);

//   const canPickDestId = destRole !== "Company";

//   useEffect(() => {
//     const list = computeAllowedDestRoles(fromType);
//     setDestRole(list[0]);
//     setFromId("");
//     setDestId("");
//   }, [fromType]);

//   const handleSubmit = () => {
//     if (!fromId) return alert("Select 'From Entity' by ID.");
//     if (!startKey || !endKey) return alert("Enter key range.");
//     if (canPickDestId && !destId) return alert("Select a destination entity.");

//     alert(`Pulled back ${keyFamily} keys ${startKey}…${endKey} from ${fromType} ${fromId}\nAssigned to ${destRole}${canPickDestId ? ` ${destId}` : " (Company)"}\n${reason ? `Reason: ${reason}` : ""}`);

//     setFromId(""); setStartKey(""); setEndKey(""); setDestId(""); setReason("");
//   };

//   return (
//     <Card brand={BRAND} title="Key Pull Back (with Re‑assignment)">
//       <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
//         <select value={fromType} onChange={(e) => setFromType(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
//           <option>Retailer</option>
//           <option>Micro Distributor</option>
//         </select>
//         <div className="sm:col-span-2">
//           <EntitySearchSelect options={entityPool[fromType]} value={fromId} onChange={setFromId} placeholder={`Search ${fromType}…`} />
//         </div>
//         <select value={keyFamily} onChange={(e) => setKeyFamily(e.target.value)} className="rounded-lg border px-3 py-2 text-sm"><option>Regular</option><option>Smart</option></select>
//         <input value={startKey} onChange={(e) => setStartKey(e.target.value)} placeholder="Start Key" className="rounded-lg border px-3 py-2 text-sm" />
//         {/* <input value={endKey} onChange={(e) => setEndKey(e.target.value)} placeholder="End Key" className="rounded-lg border px-3 py-2 text-sm" /> */}

//         <div className="sm:col-span-6 grid grid-cols-1 gap-3 sm:grid-cols-6">
//           <select value={destRole} onChange={(e) => { setDestRole(e.target.value); setDestId(""); }} className="rounded-lg border px-3 py-2 text-sm">
//             {allowedDestRoles.map((r) => (<option key={r}>{r}</option>))}
//           </select>
//           {canPickDestId ? (
//             <div className="sm:col-span-3">
//               <EntitySearchSelect options={entityPool[destRole]} value={destId} onChange={setDestId} placeholder={`Search ${destRole}…`} />
//             </div>
//           ) : (
//             <div className="sm:col-span-3 flex items-center text-sm text-slate-600">Assigning back to <b className="ml-1">Company</b></div>
//           )}
//           <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="sm:col-span-2 rounded-lg border px-3 py-2 text-sm" />
//           <div className="sm:col-span-6 flex items-center justify-end gap-2">
//             <RedButton brand={BRAND} onClick={() => { setFromId(""); setDestId(""); setStartKey(""); setEndKey(""); setReason(""); }}>Reset</RedButton>
//             <BlueButton brand={BRAND} onClick={handleSubmit}>Pull Back & Assign</BlueButton>
//           </div>
//         </div>
//       </div>
//     </Card>
//   );
// }